import { Stack, Text } from '@mantine/core';
import { formatGeometry, type PrintFormatSlug } from '@vendor/labels';

/** Props for {@link LabelSizePreview}. */
export interface LabelSizePreviewProps {
  format: PrintFormatSlug;
}

/**
 * LabelSizePreview — a bordered box drawn at a label format's real physical
 * size via CSS `in` units, so a 1-inch round sticker visibly cannot hold a
 * set name before printing thirty of them (vendor#411 / vendor#350, the
 * design's own rationale for both the print-queue drawer and the card
 * detail pane).
 *
 * Shell: any (desktop / mobile). Pure presentational — reads geometry from
 * `formatGeometry` in `@vendor/labels`, never re-derives it.
 *
 * Non-obvious contracts:
 *   - CSS inches follow the browser's 96px/in reference pixel, the same
 *     convention any browser print-preview relies on — not a per-monitor DPI
 *     measurement, but the standard web approximation of "actual size".
 */
export function LabelSizePreview({ format }: LabelSizePreviewProps) {
  const { widthIn, heightIn } = formatGeometry(format);
  return (
    <Stack gap={4} align="center">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.04em' }}>
        Actual size
      </Text>
      <div
        style={{
          width: `${widthIn.toString()}in`,
          height: `${heightIn.toString()}in`,
          border: '1px dashed var(--mantine-color-default-border)',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--mantine-color-default-hover)',
        }}
      >
        <Text size="9px" c="dimmed" ta="center" style={{ lineHeight: 1.1, padding: 2 }}>
          {widthIn.toFixed(2)}&Prime; × {heightIn.toFixed(2)}&Prime;
        </Text>
      </div>
    </Stack>
  );
}
